import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { InfiniteScrollTriggerComponent } from '@vendasta/galaxy/infinite-scroll-trigger';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { Namespace, Session } from '@vendasta/protos/ai_assistants/v1alpha1';
import { catchError, EMPTY, mergeMap, of, tap, switchMap, Observable } from 'rxjs';

import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { groupSessionsByRecency } from './session-groups';
import { SessionHistoryService } from './session-history.service';
import { MatButtonModule } from '@angular/material/button';

const DEFAULT_PAGE_SIZE = 20;
const PREVIEW_CONCURRENCY = 6;

@Component({
  selector: 'ai-generation-chat-history',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    InfiniteScrollTriggerComponent,
    GalaxyLoadingSpinnerModule,
    AiAssistantI18nModule,
  ],
  templateUrl: './generation-chat-history.component.html',
  styleUrl: './generation-chat-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenerationChatHistoryComponent {
  private readonly historyService = inject(SessionHistoryService);
  private readonly destroyRef = inject(DestroyRef);

  readonly namespace = input<Namespace | null>(null);
  readonly tags = input<string[]>([]);
  readonly activeSessionId = input<string | null>(null);
  readonly pageSize = input<number>(DEFAULT_PAGE_SIZE);

  readonly sessionSelected = output<string>();
  readonly newChatRequested = output<void>();
  readonly closed = output<void>();

  protected readonly sessions = signal<Session[]>([]);
  protected readonly previews = signal<Record<string, string | null>>({});
  protected readonly loading = signal(false);
  protected readonly nextCursor = signal<string>('');
  protected readonly hasLoadedOnce = signal(false);
  private fetchGeneration = 0;

  protected readonly groupedSessions = computed(() => groupSessionsByRecency(this.sessions()));
  protected readonly isEmpty = computed(() => this.hasLoadedOnce() && !this.loading() && this.sessions().length === 0);

  constructor() {
    effect(() => {
      const ns = this.namespace();
      const tags = this.tags();
      if (!ns || tags.length === 0) {
        this.reset();
        return;
      }
      this.reload(ns, tags);
    });
  }

  onSessionClick(sessionId: string): void {
    this.sessionSelected.emit(sessionId);
  }

  onNewChatClick(): void {
    this.newChatRequested.emit();
  }

  onCloseClick(): void {
    this.closed.emit();
  }

  loadMore(): void {
    const ns = this.namespace();
    const tags = this.tags();
    const cursor = this.nextCursor();
    if (!ns || tags.length === 0 || !cursor || this.loading()) return;
    this.fetchPage(ns, tags, cursor, /*append*/ true);
  }

  protected trackBySessionId(_: number, s: Session): string {
    return s.sessionId;
  }

  private reset(): void {
    this.fetchGeneration++;
    this.sessions.set([]);
    this.previews.set({});
    this.nextCursor.set('');
    this.hasLoadedOnce.set(false);
    this.loading.set(false);
  }

  private reload(namespace: Namespace, tags: string[]): void {
    this.fetchGeneration++;
    this.sessions.set([]);
    this.previews.set({});
    this.nextCursor.set('');
    this.fetchPage(namespace, tags, '', /*append*/ false);
  }

  private fetchPage(namespace: Namespace, tags: string[], cursor: string, append: boolean): void {
    const generation = this.fetchGeneration;
    this.loading.set(true);
    this.historyService
      .listSessions$(namespace, tags, { pageSize: this.pageSize(), cursor })
      .pipe(
        switchMap((page) => {
          if (generation !== this.fetchGeneration) return of(null);
          const merged = append ? [...this.sessions(), ...page.sessions] : page.sessions;
          this.sessions.set(merged);
          this.nextCursor.set(page.nextCursor);
          return this.fetchPreviews(namespace, page.sessions, generation);
        }),
        catchError(() => {
          if (generation === this.fetchGeneration) {
            this.loading.set(false);
            this.hasLoadedOnce.set(true);
          }
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (generation !== this.fetchGeneration) return;
        this.hasLoadedOnce.set(true);
        this.loading.set(false);
      });
  }

  private fetchPreviews(namespace: Namespace, sessions: Session[], generation: number): Observable<string | null> {
    if (sessions.length === 0) return of(null);
    return of(...sessions)
      .pipe(
        mergeMap(
          (s) =>
            this.historyService.getLastMessagePreview$(namespace, s.sessionId).pipe(
              tap((preview) => {
                if (generation !== this.fetchGeneration) return;
                this.previews.update((prev) => ({ ...prev, [s.sessionId]: preview }));
              }),
            ),
          PREVIEW_CONCURRENCY,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
  }
}
